import { useState, useEffect } from "react";
import { IworkingHours, IShift, IDaySchedule } from "@/interfaces/Ibusiness";
import toast from "react-hot-toast";
import TimezoneSelector from "./TimezoneSelector";

interface BusinessHoursSelectorProps {
  initialWorkingHours?: IworkingHours;
  initialTimezone?: string;
  onSave: (workingHours: IworkingHours, timezone: string) => Promise<void>;
  isLoading?: boolean;
  className?: string;
  showSaveCancelButtons?: boolean;
  onTimezoneChange?: (timezone: string) => void;
}

const defaultWorkingHours: IworkingHours = {
  mo: null,
  tu: null,
  we: null,
  th: null,
  fr: null,
  sa: null,
  su: null,
};

const dayMapping: { [key: string]: string } = {
  mo: "Monday",
  tu: "Tuesday",
  we: "Wednesday",
  th: "Thursday",
  fr: "Friday",
  sa: "Saturday",
  su: "Sunday",
};

// Convert minutes to HH:MM AM/PM format
const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
};

// Convert HH:MM AM/PM to minutes
const timeToMinutes = (time: string): number => {
  const [timeStr, period] = time.split(" ");
  const [hoursStr, minutesStr] = timeStr.split(":");
  const hours = parseInt(hoursStr);
  const minutes = parseInt(minutesStr);

  let totalMinutes = minutes;
  if (hours === 12) {
    totalMinutes += period === "AM" ? 0 : 12 * 60;
  } else {
    totalMinutes += hours * 60;
    if (period === "PM") totalMinutes += 12 * 60;
  }

  return totalMinutes;
};

const isTimeOverlapping = (shift1: IShift, shift2: IShift | null): boolean => {
  if (!shift2 || !shift1.start || !shift1.end || !shift2.start || !shift2.end)
    return false;
  return (
    (shift1.start < shift2.end && shift1.end > shift2.start) ||
    (shift2.start < shift1.end && shift2.end > shift1.start)
  );
};

const sortShifts = (shifts: (IShift | null)[] | null | undefined): IShift[] => {
  if (!shifts) return [];
  const validShifts = shifts.filter(
    (shift): shift is NonNullable<IShift> =>
      shift !== null &&
      typeof shift.start === "number" &&
      typeof shift.end === "number"
  );
  return validShifts.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
};

const validateNewShiftTime = (
  shifts: (IShift | null)[] | null | undefined,
  currentShiftIndex: number,
  newStart: number,
  newEnd: number
): boolean => {
  if (newEnd <= newStart) return false;
  if (!shifts) return true;

  return !shifts.some((shift, index) => {
    if (index === currentShiftIndex || !shift) return false;
    return isTimeOverlapping({ start: newStart, end: newEnd }, shift);
  });
};

// Helper to get the ordered days array: business days first, then Saturday and Sunday
const getOrderedDays = () => {
  return ["mo", "tu", "we", "th", "fr", "sa", "su"];
};

// Helper to get display label for each day
const getDayLabel = (day: string): string => {
  return dayMapping[day] || day;
};

export default function BusinessHoursSelector({
  initialWorkingHours = defaultWorkingHours,
  initialTimezone = "UTC",
  onSave,
  isLoading = false,
  className = "",
  showSaveCancelButtons = true,
  onTimezoneChange,
}: BusinessHoursSelectorProps) {
  if (!initialWorkingHours) return null;
  const [workingHours, setWorkingHours] =
    useState<IworkingHours>(initialWorkingHours);
  const [tempWorkingHours, setTempWorkingHours] =
    useState<IworkingHours>(initialWorkingHours);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimezone, setSelectedTimezone] =
    useState<string>(initialTimezone);

  // Update both states when initialWorkingHours changes
  useEffect(() => {
    if (initialWorkingHours) {
      const newWorkingHours = { ...initialWorkingHours };
      setWorkingHours(newWorkingHours);
      setTempWorkingHours(newWorkingHours);
    }
  }, [initialWorkingHours]);

  // Update timezone when initialTimezone changes
  useEffect(() => {
    console.log(
      "BusinessHoursSelector - initialTimezone changed to:",
      initialTimezone
    );
    setSelectedTimezone(initialTimezone);
  }, [initialTimezone]);

  // Debug logging for selectedTimezone changes and notify parent
  useEffect(() => {
    console.log(
      "BusinessHoursSelector - selectedTimezone changed to:",
      selectedTimezone
    );
    onTimezoneChange?.(selectedTimezone);
  }, [selectedTimezone, onTimezoneChange]);

  // Generate time options in 15-minute intervals
  const generateTimeOptions = () => {
    const options: string[] = [];
    for (let i = 0; i < 24 * 60; i += 15) {
      options.push(minutesToTime(i));
    }
    return options;
  };

  // Get available time options considering existing shifts
  const getAvailableTimeOptions = (
    day: string,
    currentShiftIndex: number,
    field: "start" | "end",
    allTimeOptions: string[]
  ) => {
    const daySchedule = tempWorkingHours[day];
    if (!daySchedule?.shifts) return allTimeOptions;

    const currentShift = daySchedule.shifts[currentShiftIndex];
    const otherShifts = daySchedule.shifts.filter(
      (_, index) => index !== currentShiftIndex
    );

    return allTimeOptions.filter((timeStr) => {
      const timeInMinutes = timeToMinutes(timeStr);

      if (field === "start" && currentShift?.end) {
        if (timeInMinutes >= currentShift.end) return false;
      }

      if (field === "end" && currentShift?.start) {
        if (timeInMinutes <= currentShift.start) return false;
      }

      for (const shift of otherShifts) {
        if (!shift?.start || !shift?.end) continue;

        if (field === "start") {
          if (timeInMinutes >= shift.start && timeInMinutes < shift.end)
            return false;
          if (currentShift?.end) {
            if (timeInMinutes < shift.end && currentShift.end > shift.start)
              return false;
          }
        }

        if (field === "end") {
          if (timeInMinutes > shift.start && timeInMinutes <= shift.end)
            return false;
          if (currentShift?.start) {
            if (currentShift.start < shift.end && timeInMinutes > shift.start)
              return false;
          }
        }
      }

      return true;
    });
  };

  const timeOptions = generateTimeOptions();

  const handleToggleDay = (day: string) => {
    setTempWorkingHours((prev) => ({
      ...prev,
      [day]:
        prev[day] === null
          ? { start: 480, end: 1020, shifts: [{ start: 480, end: 1020 }] }
          : null,
    }));
  };

  const handleShiftChange = (
    day: string,
    shiftIndex: number,
    field: "start" | "end",
    value: string
  ) => {
    setTempWorkingHours((prev) => {
      const daySchedule = prev[day];
      if (!daySchedule?.shifts) return prev;

      const currentShift = daySchedule.shifts[shiftIndex];
      if (!currentShift) return prev;

      const minutes = timeToMinutes(value);
      const newStart = field === "start" ? minutes : currentShift.start ?? 0;
      const newEnd = field === "end" ? minutes : currentShift.end ?? 0;

      if (
        !validateNewShiftTime(daySchedule.shifts, shiftIndex, newStart, newEnd)
      ) {
        return prev;
      }

      const newShifts = [...daySchedule.shifts];
      newShifts[shiftIndex] = {
        ...currentShift,
        [field]: minutes,
      };

      return {
        ...prev,
        [day]: {
          ...daySchedule,
          shifts: sortShifts(newShifts),
        },
      };
    });
  };

  const addShift = (day: string) => {
    setTempWorkingHours((prev) => {
      const daySchedule = prev[day];
      if (!daySchedule?.shifts) return prev;

      const existingShifts = daySchedule.shifts;
      let newShiftStart = 480; // 8:00 AM
      let newShiftEnd = 1020; // 5:00 PM

      const sortedShifts = sortShifts(existingShifts);
      for (let i = 0; i < sortedShifts.length; i++) {
        const currentShift = sortedShifts[i];
        const nextShift = sortedShifts[i + 1];

        if (!currentShift?.end) continue;

        if (!nextShift) {
          if (currentShift.end + 60 <= 1380) {
            newShiftStart = currentShift.end + 60;
            newShiftEnd = Math.min(newShiftStart + 540, 1380);
            break;
          }
        } else if (nextShift.start) {
          const gap = nextShift.start - currentShift.end;
          if (gap >= 120) {
            newShiftStart = currentShift.end + 60;
            newShiftEnd = nextShift.start - 60;
            break;
          }
        }
      }

      const firstShift = sortedShifts[0];
      if (
        !sortedShifts.length ||
        (firstShift?.start && firstShift.start >= 540)
      ) {
        newShiftStart = 360;
        newShiftEnd = 480;
      }

      if (
        !validateNewShiftTime(existingShifts, -1, newShiftStart, newShiftEnd)
      ) {
        return prev;
      }

      const newShifts = Array.isArray(existingShifts)
        ? [...existingShifts]
        : [];
      newShifts.push({ start: newShiftStart, end: newShiftEnd });

      return {
        ...prev,
        [day]: {
          ...daySchedule,
          shifts: sortShifts(newShifts),
        },
      };
    });
  };

  const removeShift = (day: string, shiftIndex: number) => {
    setTempWorkingHours((prev) => {
      const daySchedule = prev[day];
      if (!daySchedule?.shifts) return prev;

      const newShifts = daySchedule.shifts.filter(
        (_, index) => index !== shiftIndex
      );

      // If this was the last shift, set the day to null (day off)
      if (newShifts.length === 0) {
        return {
          ...prev,
          [day]: null,
        };
      }

      return {
        ...prev,
        [day]: {
          ...daySchedule,
          shifts: newShifts,
        },
      };
    });
  };

  const handleSave = async () => {
    try {
      setError(null);
      console.log(
        "BusinessHoursSelector handleSave - selectedTimezone:",
        selectedTimezone
      );
      await onSave(tempWorkingHours, selectedTimezone);
      // Update both states after successful save
      setWorkingHours({ ...tempWorkingHours });
      setTempWorkingHours({ ...tempWorkingHours });
      if (showSaveCancelButtons) {
        toast.success("Business hours saved successfully");
      }
    } catch (err) {
      setError("Failed to save business hours. Please try again later.");
    }
  };

  const handleCancel = () => {
    setTempWorkingHours(workingHours);
  };

  const handleApplyToAll = () => {
    const templateDay = Object.values(tempWorkingHours).find(
      (day) => day !== null
    );
    if (!templateDay) return;

    setTempWorkingHours((prev) => {
      const newHours = { ...prev };
      Object.keys(newHours).forEach((day) => {
        if (newHours[day] !== null) {
          newHours[day] = {
            ...templateDay,
            shifts: [...(templateDay.shifts ?? [])],
          };
        }
      });
      return newHours;
    });
  };

  const renderShiftSelect = (
    schedule: IDaySchedule | null,
    day: string,
    shiftIndex: number,
    field: "start" | "end"
  ) => {
    if (!schedule?.shifts?.[shiftIndex]) return null;

    const value =
      schedule.shifts[shiftIndex][field] != null
        ? minutesToTime(schedule.shifts[shiftIndex][field] as number)
        : field === "start"
        ? "12:00 AM"
        : "12:00 PM";

    return (
      <select
        value={value}
        onChange={(e) =>
          handleShiftChange(day, shiftIndex, field, e.target.value)
        }
        className="bg-white border rounded-md px-3 py-1.5 w-[120px]"
      >
        {getAvailableTimeOptions(day, shiftIndex, field, timeOptions).map(
          (time) => (
            <option key={time} value={time}>
              {time}
            </option>
          )
        )}
      </select>
    );
  };

  if (isLoading) {
    return (
      <div className={`max-w-2xl mx-auto p-4 ${className}`}>
        <div className="animate-pulse space-y-4">
          {[...Array(7)].map((_, index) => (
            <div key={index} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full p-4 ${className}`}>
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <div className="flex justify-end mb-4">
        <button
          onClick={handleApplyToAll}
          className="text-green-600 hover:text-green-700 font-medium"
        >
          Apply to all
        </button>
      </div>

      {getOrderedDays().map((day) => {
        const schedule = tempWorkingHours[day];
        return (
          <div
            key={day}
            className="flex items-center py-4 border-b last:border-b-0"
          >
            <div className="w-[200px]">
              <span className={`text-base font-medium capitalize`}>
                {getDayLabel(day)}
              </span>
            </div>
            {schedule ? (
              <div className="flex flex-col gap-2 flex-1">
                {(schedule.shifts ?? []).map((_, shiftIdx) => (
                  <div key={shiftIdx} className="flex items-center gap-2">
                    {renderShiftSelect(schedule, day, shiftIdx, "start")}
                    <span className="text-gray-500 px-2">-</span>
                    {renderShiftSelect(schedule, day, shiftIdx, "end")}
                    <button
                      onClick={() => removeShift(day, shiftIdx)}
                      className="text-red-400 hover:text-red-600"
                      title="Remove hours"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {shiftIdx === (schedule.shifts?.length ?? 0) - 1 && (
                      <button
                        onClick={() => addShift(day)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Add shift"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex-1 flex items-center">
                <button
                  onClick={() => handleToggleDay(day)}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm ml-0"
                  style={{ marginLeft: 0 }}
                >
                  Add hours
                </button>
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-6">
        <h3 className="text-sm mb-2">Time zone</h3>
        <TimezoneSelector
          value={selectedTimezone}
          onChange={setSelectedTimezone}
          variant="compact"
          showLabel={false}
        />
      </div>

      {showSaveCancelButtons && (
        <div className="mt-6 flex justify-between">
          <button
            onClick={handleCancel}
            className="px-6 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}
